import React, { useEffect, useState } from "react";
import { api } from "@finance/lib/api";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";

// Module-level cache so we fetch the modes map only once per session.
let _modesCache = null;
let _inflight = null;
const _subs = new Set();

export async function refreshNumberingModes() {
  _inflight = api.get("/document-numbering/modes").then((r) => {
    _modesCache = r.data || {};
    _subs.forEach((fn) => fn(_modesCache));
    return _modesCache;
  }).catch(() => ({}));
  return _inflight;
}

export function useNumberingModes() {
  const [modes, setModes] = useState(_modesCache || {});
  useEffect(() => {
    const sub = (m) => setModes({ ...m });
    _subs.add(sub);
    if (_modesCache) setModes(_modesCache);
    else if (_inflight) _inflight.then((m) => setModes({ ...m }));
    else refreshNumberingModes();
    return () => { _subs.delete(sub); };
  }, []);
  return modes;
}

export function isManual(modes, docKey) {
  return (modes || {})[docKey] === "manual";
}

export function isOff(modes, docKey) {
  return (modes || {})[docKey] === "off";
}

/**
 * Renders a document-number field for `docKey`.
 * Manual mode → editable, required input. Automated → read-only "Auto-generated on save".
 * Off → numbering disabled for this document type (no number issued).
 */
export function DupCheck({ kind, value }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    const v = (value || "").trim();
    if (!v) { setSt(null); return undefined; }
    const t = setTimeout(() => api.get("/document-numbering/check", { params: { kind, value: v } }).then((r) => setSt(r.data.exists)).catch(() => setSt(null)), 400);
    return () => clearTimeout(t);
  }, [kind, value]);
  if (st === null) return null;
  return <div className={`mt-1 text-xs ${st ? "text-red-600" : "text-emerald-600"}`} data-testid={`dup-check-${kind}`}>{st ? "Already exists" : "Available"}</div>;
}

export function DocNumberField({ docKey, value, onChange, testid, label = "Document Number" }) {
  const modes = useNumberingModes();
  const manual = isManual(modes, docKey);
  const off = isOff(modes, docKey);
  return (
    <div>
      <Label className="lx-label">{label}{manual ? " *" : ""}</Label>
      {off ? (
        <Input className="mt-1 bg-gray-50 italic text-gray-400" data-testid={testid} value="Numbering disabled" readOnly disabled />
      ) : manual ? (
        <Input className="mt-1" data-testid={testid} value={value || ""} placeholder="Enter document number"
          onChange={(e) => onChange?.(e.target.value)} />
      ) : (
        <Input className="mt-1 bg-gray-50 text-gray-500" data-testid={testid} value={value || "Auto-generated on save"} readOnly disabled />
      )}
      {manual && <DupCheck kind={docKey} value={value} />}
    </div>
  );
}
